import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colors
const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgSecondary: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
  success: '#10B981',
  successBg: '#ECFDF5',
  warning: '#F59E0B',
  warningBg: '#FFFBEB',
  error: '#EF4444',
  errorBg: '#FEF2F2',
};

const STATUS_LABELS = {
  NEW: { label: "جديد", bg: '#dbeafe', color: '#1d4ed8' },
  ASSIGNED: { label: "مُسند", bg: '#fef3c7', color: '#b45309' },
  SCHEDULED: { label: "مجدول", bg: '#f3e8ff', color: '#7c3aed' },
  COMPLETED: { label: "مكتمل", bg: '#dcfce7', color: '#15803d' },
  CLOSED: { label: "مغلق", bg: '#f1f5f9', color: '#475569' },
};

const JOURNEY_TYPES = [
  { value: "consultation_journey", label: "رحلة استشارية" },
  { value: "training_journey", label: "رحلة تدريبية" },
  { value: "workspace_journey", label: "رحلة مساحة عمل" },
  { value: "sales_outlet_journey", label: "رحلة منفذ بيع" },
  { value: "growth_program_journey", label: "رحلة برنامج تطوير" },
  { value: "event_journey", label: "رحلة فعالية" },
];

const PROJECT_STAGES = ["مشروع_قائم", "فكرة_مشروع", "عمل_حر", "صعوبات_تشغيل", "توسعة", "أسرة_منتجة"];
const MAIN_PROBLEMS = ["ضعف_المبيعات", "ضعف_التسويق", "صعوبة_الإدارة", "مشاكل_التشغيل", "تطوير_المنتج", "التسعير_والمالية"];
const SUPPORT_TYPES = ["مستشار", "تدريب", "بيئة_عمل", "فرص_بيع", "أكثر_من_نوع"];
const SECTORS = ["أطعمة_ومشروبات", "تجارة_ومنتجات", "خدمات", "متجر_إلكتروني", "إنتاج_منزلي", "تقنية"];
const ICONS = ["MessageSquare", "GraduationCap", "Rocket", "Building2", "Store", "Calendar", "Laptop", "Home"];

function getAuthHeaders() {
  const token = localStorage.getItem("hala_admin_token");
  return { Authorization: `Bearer ${token}` };
}

// ─── Tab Component ────────────────────────────────────────────────────────────
function Tab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: 500,
        borderBottom: active ? `2px solid ${COLORS.primary}` : '2px solid transparent',
        color: active ? COLORS.primary : COLORS.textMuted,
        background: active ? COLORS.white : 'transparent',
        border: 'none',
        borderRadius: '8px 8px 0 0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

// ─── Modal Wrapper ────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }} dir="rtl">
      <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px', borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
          <h2 style={{ fontWeight: 'bold', fontSize: '18px', margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: COLORS.bgSecondary, cursor: 'pointer', fontSize: '16px' }}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Multi-Select Component ───────────────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOption = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };

  return (
    <div style={{ position: 'relative', marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: COLORS.text, marginBottom: '4px' }}>{label}</label>
      <div onClick={() => setIsOpen(!isOpen)} style={{ minHeight: '36px', width: '100%', borderRadius: '8px', border: `1px solid ${COLORS.border}`, padding: '6px 12px', fontSize: '14px', backgroundColor: COLORS.bgSecondary, cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
        {selected.length === 0 ? (
          <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>{placeholder || "اختر..."}</span>
        ) : (
          selected.map(s => (<span key={s} style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary, fontSize: '10px', padding: '2px 8px', borderRadius: '9999px' }}>{s}</span>))
        )}
      </div>
      {isOpen && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: '4px', backgroundColor: 'white', border: `1px solid ${COLORS.border}`, borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', maxHeight: '192px', overflowY: 'auto' }}>
          {options.map(opt => (
            <div key={opt} onClick={() => toggleOption(opt)} style={{ padding: '8px 12px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: selected.includes(opt) ? `${COLORS.primary}08` : 'transparent' }}>
              <span style={{ width: '16px', height: '16px', borderRadius: '4px', border: `1px solid ${selected.includes(opt) ? COLORS.primary : COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: selected.includes(opt) ? COLORS.primary : 'transparent', color: 'white', fontSize: '10px' }}>
                {selected.includes(opt) && "✓"}
              </span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Requests Tab ────────────────────────────────────────────────────────────
function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/requests`, { headers: getAuthHeaders() });
      setRequests(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/requests/${requestId}/status`, { status }, { headers: getAuthHeaders() });
      setRequests((prev) => prev.map((r) => r.request_id === requestId ? { ...r, status } : r));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.textMuted, fontSize: '14px' }}>جاري التحميل...</div>;

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: '14px', color: COLORS.textMuted }}>إجمالي الطلبات: <span style={{ fontWeight: 'bold', color: COLORS.text }}>{requests.length}</span></p>
        <button onClick={load} style={{ fontSize: '12px', color: COLORS.primary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>تحديث</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {requests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', color: COLORS.textMuted, backgroundColor: COLORS.bgSecondary, borderRadius: '12px' }}>لا توجد طلبات بعد</div>
        ) : requests.map((r) => (
          <div key={r.request_id} style={{ borderRadius: '12px', border: `1px solid ${COLORS.border}`, backgroundColor: 'white', overflow: 'hidden' }}>
            <div onClick={() => setExpandedId(expandedId === r.request_id ? null : r.request_id)} style={{ padding: '16px', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', color: COLORS.primary, fontWeight: 500 }}>{r.request_id}</span>
                    <span style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '9999px', backgroundColor: STATUS_LABELS[r.status]?.bg || COLORS.bgSecondary, color: STATUS_LABELS[r.status]?.color || COLORS.textMuted }}>
                      {STATUS_LABELS[r.status]?.label || r.status}
                    </span>
                  </div>
                  <p style={{ fontWeight: 500, fontSize: '14px', margin: 0 }}>{r.client_name || r.user_name}</p>
                  <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0 }}>{r.service_name_ar} • {r.consultant_name || "-"}</p>
                </div>
                <div style={{ textAlign: 'left', flexShrink: 0 }}>
                  <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0 }}>{new Date(r.created_at).toLocaleDateString("ar-SA")}</p>
                  <select value={r.status} onChange={(e) => { e.stopPropagation(); updateStatus(r.request_id, e.target.value); }} onClick={(e) => e.stopPropagation()}
                    style={{ marginTop: '4px', fontSize: '12px', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '4px 8px', outline: 'none', backgroundColor: 'white' }}>
                    {Object.keys(STATUS_LABELS).map((s) => (<option key={s} value={s}>{STATUS_LABELS[s].label}</option>))}
                  </select>
                </div>
              </div>
            </div>
            
            {expandedId === r.request_id && (
              <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: '16px', backgroundColor: COLORS.bgSecondary, fontSize: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div><span style={{ color: COLORS.textMuted }}>الجوال:</span> <span style={{ fontWeight: 500 }}>{r.mobile_number}</span></div>
                  <div><span style={{ color: COLORS.textMuted }}>البريد:</span> <span style={{ fontWeight: 500 }}>{r.email || "-"}</span></div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Categories Tab ───────────────────────────────────────────────────────────
function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/categories`, { headers: getAuthHeaders() });
      setCategories(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editItem) await axios.put(`${API}/admin/categories/${editItem.category_id}`, data, { headers: getAuthHeaders() });
      else await axios.post(`${API}/admin/categories`, data, { headers: getAuthHeaders() });
      await load(); setShowModal(false);
    } catch (e) { console.error(e); alert("حدث خطأ"); }
  };

  const toggleActive = async (categoryId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/categories/${categoryId}`, { is_active: !currentStatus }, { headers: getAuthHeaders() });
      setCategories(prev => prev.map(c => c.category_id === categoryId ? {...c, is_active: !currentStatus} : c));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.textMuted }}>جاري التحميل...</div>;

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: '14px', color: COLORS.textMuted }}>الفئات: <span style={{ fontWeight: 'bold', color: COLORS.text }}>{categories.length}</span></p>
        <button onClick={() => { setEditItem(null); setShowModal(true); }} style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 500, border: 'none', cursor: 'pointer' }}>إضافة فئة</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {categories.map((c) => (
          <div key={c.category_id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, backgroundColor: c.is_active ? 'white' : COLORS.bgSecondary, opacity: c.is_active ? 1 : 0.6 }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: `${COLORS.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.primary, fontWeight: 'bold', flexShrink: 0 }}>📁</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 500, fontSize: '14px' }}>{c.category_name_ar}</span>
                <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', backgroundColor: c.is_active ? COLORS.successBg : COLORS.bgSecondary, color: c.is_active ? COLORS.success : COLORS.textMuted }}>{c.is_active ? "نشط" : "غير نشط"}</span>
              </div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0 }}>{c.category_name_en || "-"} • ترتيب: {c.display_order}</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button onClick={() => { setEditItem(c); setShowModal(true); }} style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '12px', backgroundColor: 'white', cursor: 'pointer' }}>تعديل</button>
              <button onClick={() => toggleActive(c.category_id, c.is_active)} style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', backgroundColor: c.is_active ? COLORS.errorBg : COLORS.successBg, color: c.is_active ? COLORS.error : COLORS.success }}>{c.is_active ? "تعطيل" : "تفعيل"}</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && <CategoryModal category={editItem} onSave={handleSave} onClose={() => setShowModal(false)} />}
    </div>
  );
}

function CategoryModal({ category, onSave, onClose }) {
  const [form, setForm] = useState({
    category_name_ar: category?.category_name_ar || "",
    category_name_en: category?.category_name_en || "",
    icon: category?.icon || "MessageSquare",
    display_order: category?.display_order || 0,
    is_active: category?.is_active ?? true,
  });

  const inputStyle = { width: '100%', height: '36px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, padding: '0 12px', fontSize: '14px', outline: 'none', backgroundColor: COLORS.bgSecondary };

  return (
    <Modal title={category ? "تعديل فئة" : "إضافة فئة جديدة"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, display_order: parseInt(form.display_order) }); }} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>اسم الفئة بالعربي *</label>
          <input type="text" value={form.category_name_ar} onChange={(e) => setForm(p => ({ ...p, category_name_ar: e.target.value }))} required style={inputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>اسم الفئة بالإنجليزي</label>
          <input type="text" value={form.category_name_en} onChange={(e) => setForm(p => ({ ...p, category_name_en: e.target.value }))} style={inputStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>الأيقونة</label>
            <select value={form.icon} onChange={(e) => setForm(p => ({ ...p, icon: e.target.value }))} style={inputStyle}>
              {ICONS.map(icon => (<option key={icon} value={icon}>{icon}</option>))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>ترتيب العرض</label>
            <input type="number" min="0" value={form.display_order} onChange={(e) => setForm(p => ({ ...p, display_order: e.target.value }))} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
          <button type="submit" style={{ flex: 1, height: '40px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>{category ? "حفظ التعديلات" : "إضافة الفئة"}</button>
          <button type="button" onClick={onClose} style={{ flex: 1, height: '40px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '14px', backgroundColor: 'white', cursor: 'pointer' }}>إلغاء</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Services Tab ─────────────────────────────────────────────────────────────
function ServicesTab() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const [svcRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/services`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/categories`, { headers: getAuthHeaders() }),
      ]);
      setServices(svcRes.data); setCategories(catRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getCategoryName = (catId) => categories.find(c => c.category_id === catId)?.category_name_ar || catId;

  const handleSave = async (data) => {
    try {
      if (editItem) await axios.put(`${API}/admin/services/${editItem.service_id}`, data, { headers: getAuthHeaders() });
      else await axios.post(`${API}/admin/services`, data, { headers: getAuthHeaders() });
      await load(); setShowModal(false);
    } catch (e) { console.error(e); alert("حدث خطأ"); }
  };

  const toggleActive = async (serviceId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/services/${serviceId}`, { is_active: !currentStatus }, { headers: getAuthHeaders() });
      setServices(prev => prev.map(s => s.service_id === serviceId ? {...s, is_active: !currentStatus} : s));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.textMuted }}>جاري التحميل...</div>;

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: '14px', color: COLORS.textMuted }}>الخدمات: <span style={{ fontWeight: 'bold', color: COLORS.text }}>{services.length}</span></p>
        <button onClick={() => { setEditItem(null); setShowModal(true); }} style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 500, border: 'none', cursor: 'pointer' }}>إضافة خدمة</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {services.map((s) => (
          <div key={s.service_id} style={{ padding: '16px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, backgroundColor: s.is_active ? 'white' : COLORS.bgSecondary, opacity: s.is_active ? 1 : 0.6 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: 500, fontSize: '14px' }}>{s.service_name_ar}</span>
                  <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', backgroundColor: s.is_active ? COLORS.successBg : COLORS.bgSecondary, color: s.is_active ? COLORS.success : COLORS.textMuted }}>{s.is_active ? "نشط" : "غير نشط"}</span>
                </div>
                <p style={{ fontSize: '12px', color: COLORS.textMuted, marginBottom: '8px' }}>{s.short_description_ar}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '9999px', backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}>{getCategoryName(s.service_category_id)}</span>
                  <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '9999px', backgroundColor: COLORS.bgSecondary, color: COLORS.textMuted }}>{JOURNEY_TYPES.find(j => j.value === s.journey_type)?.label || s.journey_type}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => { setEditItem(s); setShowModal(true); }} style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '12px', backgroundColor: 'white', cursor: 'pointer' }}>تعديل</button>
                <button onClick={() => toggleActive(s.service_id, s.is_active)} style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', backgroundColor: s.is_active ? COLORS.errorBg : COLORS.successBg, color: s.is_active ? COLORS.error : COLORS.success }}>{s.is_active ? "تعطيل" : "تفعيل"}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && <ServiceModal service={editItem} categories={categories} onSave={handleSave} onClose={() => setShowModal(false)} />}
    </div>
  );
}

function ServiceModal({ service, categories, onSave, onClose }) {
  const [form, setForm] = useState({
    service_code: service?.service_code || "",
    service_name_ar: service?.service_name_ar || "",
    service_category_id: service?.service_category_id || categories[0]?.category_id || "",
    short_description_ar: service?.short_description_ar || "",
    journey_type: service?.journey_type || "consultation_journey",
    requires_consultant_selection: service?.requires_consultant_selection || false,
    applicable_project_stages: service?.applicable_project_stages || [],
    applicable_sectors: service?.applicable_sectors || [],
    primary_problem_tags: service?.primary_problem_tags || [],
    is_active: service?.is_active ?? true,
  });

  const inputStyle = { width: '100%', height: '36px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, padding: '0 12px', fontSize: '14px', outline: 'none', backgroundColor: COLORS.bgSecondary };

  return (
    <Modal title={service ? "تعديل خدمة" : "إضافة خدمة جديدة"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>كود الخدمة *</label>
            <input type="text" value={form.service_code} onChange={(e) => setForm(p => ({ ...p, service_code: e.target.value }))} required style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>الفئة *</label>
            <select value={form.service_category_id} onChange={(e) => setForm(p => ({ ...p, service_category_id: e.target.value }))} required style={inputStyle}>
              {categories.map(c => (<option key={c.category_id} value={c.category_id}>{c.category_name_ar}</option>))}
            </select>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>اسم الخدمة بالعربي *</label>
          <input type="text" value={form.service_name_ar} onChange={(e) => setForm(p => ({ ...p, service_name_ar: e.target.value }))} required style={inputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>الوصف المختصر *</label>
          <textarea value={form.short_description_ar} onChange={(e) => setForm(p => ({ ...p, short_description_ar: e.target.value }))} required rows={2} style={{ ...inputStyle, height: 'auto', padding: '8px 12px', resize: 'none' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>نوع الرحلة *</label>
          <select value={form.journey_type} onChange={(e) => setForm(p => ({ ...p, journey_type: e.target.value }))} style={inputStyle}>
            {JOURNEY_TYPES.map(j => (<option key={j.value} value={j.value}>{j.label}</option>))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input type="checkbox" id="requires_consultant" checked={form.requires_consultant_selection} onChange={(e) => setForm(p => ({ ...p, requires_consultant_selection: e.target.checked }))} />
          <label htmlFor="requires_consultant" style={{ fontSize: '12px' }}>يتطلب اختيار مستشار</label>
        </div>
        <MultiSelect label="مراحل المشروع" options={PROJECT_STAGES} selected={form.applicable_project_stages} onChange={(val) => setForm(p => ({ ...p, applicable_project_stages: val }))} />
        <MultiSelect label="القطاعات" options={SECTORS} selected={form.applicable_sectors} onChange={(val) => setForm(p => ({ ...p, applicable_sectors: val }))} />
        <MultiSelect label="التحديات" options={MAIN_PROBLEMS} selected={form.primary_problem_tags} onChange={(val) => setForm(p => ({ ...p, primary_problem_tags: val }))} />
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
          <button type="submit" style={{ flex: 1, height: '40px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>{service ? "حفظ" : "إضافة"}</button>
          <button type="button" onClick={onClose} style={{ flex: 1, height: '40px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '14px', backgroundColor: 'white', cursor: 'pointer' }}>إلغاء</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Rules Tab ────────────────────────────────────────────────────────────────
function RulesTab() {
  const [rules, setRules] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const [rulesRes, svcRes] = await Promise.all([
        axios.get(`${API}/admin/rules`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/services`, { headers: getAuthHeaders() }),
      ]);
      setRules(rulesRes.data); setServices(svcRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getServiceName = (svcId) => services.find(s => s.service_id === svcId)?.service_name_ar || svcId;

  const handleSave = async (data) => {
    try {
      if (editItem) await axios.put(`${API}/admin/rules/${editItem.rule_id}`, data, { headers: getAuthHeaders() });
      else await axios.post(`${API}/admin/rules`, data, { headers: getAuthHeaders() });
      await load(); setShowModal(false);
    } catch (e) { console.error(e); alert("حدث خطأ"); }
  };

  const toggleActive = async (ruleId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/rules/${ruleId}`, { is_active: !currentStatus }, { headers: getAuthHeaders() });
      setRules(prev => prev.map(r => r.rule_id === ruleId ? {...r, is_active: !currentStatus} : r));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.textMuted }}>جاري التحميل...</div>;

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: '14px', color: COLORS.textMuted }}>القواعد: <span style={{ fontWeight: 'bold', color: COLORS.text }}>{rules.length}</span></p>
        <button onClick={() => { setEditItem(null); setShowModal(true); }} style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 500, border: 'none', cursor: 'pointer' }}>إضافة قاعدة</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {rules.map((r) => (
          <div key={r.rule_id} style={{ padding: '16px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, backgroundColor: r.is_active ? 'white' : COLORS.bgSecondary, opacity: r.is_active ? 1 : 0.6 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 500, fontSize: '14px' }}>{r.rule_name_ar || r.rule_id}</span>
                  <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', backgroundColor: r.is_active ? COLORS.successBg : COLORS.bgSecondary, color: r.is_active ? COLORS.success : COLORS.textMuted }}>{r.is_active ? "نشط" : "غير نشط"}</span>
                  <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', backgroundColor: COLORS.warningBg, color: COLORS.warning }}>وزن: {r.rule_weight}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '8px', backgroundColor: `${COLORS.primary}15`, color: COLORS.primary, fontWeight: 500 }}>رئيسي: {getServiceName(r.primary_service_id)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => { setEditItem(r); setShowModal(true); }} style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '12px', backgroundColor: 'white', cursor: 'pointer' }}>تعديل</button>
                <button onClick={() => toggleActive(r.rule_id, r.is_active)} style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', backgroundColor: r.is_active ? COLORS.errorBg : COLORS.successBg, color: r.is_active ? COLORS.error : COLORS.success }}>{r.is_active ? "تعطيل" : "تفعيل"}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && <RuleModal rule={editItem} services={services} onSave={handleSave} onClose={() => setShowModal(false)} />}
    </div>
  );
}

function RuleModal({ rule, services, onSave, onClose }) {
  const [form, setForm] = useState({
    rule_name_ar: rule?.rule_name_ar || "",
    project_stages: rule?.project_stages || [],
    main_problems: rule?.main_problems || [],
    support_types: rule?.support_types || [],
    sectors: rule?.sectors || [],
    primary_service_id: rule?.primary_service_id || services[0]?.service_id || "",
    secondary_service_1_id: rule?.secondary_service_1_id || "",
    rule_weight: rule?.rule_weight || 100,
    is_active: rule?.is_active ?? true,
  });

  const inputStyle = { width: '100%', height: '36px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, padding: '0 12px', fontSize: '14px', outline: 'none', backgroundColor: COLORS.bgSecondary };

  return (
    <Modal title={rule ? "تعديل قاعدة" : "إضافة قاعدة جديدة"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, rule_weight: parseInt(form.rule_weight) }); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>اسم القاعدة</label>
          <input type="text" value={form.rule_name_ar} onChange={(e) => setForm(p => ({ ...p, rule_name_ar: e.target.value }))} style={inputStyle} />
        </div>
        <div style={{ padding: '12px', backgroundColor: COLORS.bgSecondary, borderRadius: '8px' }}>
          <p style={{ fontSize: '12px', fontWeight: 500, marginBottom: '12px', color: COLORS.textMuted }}>شروط المطابقة</p>
          <MultiSelect label="مراحل المشروع" options={PROJECT_STAGES} selected={form.project_stages} onChange={(val) => setForm(p => ({ ...p, project_stages: val }))} />
          <MultiSelect label="التحديات" options={MAIN_PROBLEMS} selected={form.main_problems} onChange={(val) => setForm(p => ({ ...p, main_problems: val }))} />
          <MultiSelect label="أنواع الدعم" options={SUPPORT_TYPES} selected={form.support_types} onChange={(val) => setForm(p => ({ ...p, support_types: val }))} />
          <MultiSelect label="القطاعات" options={SECTORS} selected={form.sectors} onChange={(val) => setForm(p => ({ ...p, sectors: val }))} />
        </div>
        <div style={{ padding: '12px', backgroundColor: `${COLORS.primary}08`, borderRadius: '8px' }}>
          <p style={{ fontSize: '12px', fontWeight: 500, marginBottom: '12px', color: COLORS.primary }}>الخدمات الموصى بها</p>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>الخدمة الرئيسية *</label>
            <select value={form.primary_service_id} onChange={(e) => setForm(p => ({ ...p, primary_service_id: e.target.value }))} required style={{ ...inputStyle, backgroundColor: 'white' }}>
              {services.map(s => (<option key={s.service_id} value={s.service_id}>{s.service_name_ar}</option>))}
            </select>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>وزن القاعدة</label>
          <input type="number" min="1" max="1000" value={form.rule_weight} onChange={(e) => setForm(p => ({ ...p, rule_weight: e.target.value }))} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
          <button type="submit" style={{ flex: 1, height: '40px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>{rule ? "حفظ" : "إضافة"}</button>
          <button type="button" onClick={onClose} style={{ flex: 1, height: '40px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '14px', backgroundColor: 'white', cursor: 'pointer' }}>إلغاء</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Consultants Tab ──────────────────────────────────────────────────────────
function ConsultantsTab() {
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/consultants`, { headers: getAuthHeaders() });
      setConsultants(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editItem) await axios.put(`${API}/admin/consultants/${editItem.consultant_id}`, data, { headers: getAuthHeaders() });
      else await axios.post(`${API}/admin/consultants`, data, { headers: getAuthHeaders() });
      await load(); setShowModal(false);
    } catch (e) { console.error(e); alert("حدث خطأ"); }
  };

  const toggleActive = async (consultantId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/consultants/${consultantId}`, { is_active: !currentStatus }, { headers: getAuthHeaders() });
      setConsultants(prev => prev.map(c => c.consultant_id === consultantId ? {...c, is_active: !currentStatus} : c));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.textMuted }}>جاري التحميل...</div>;

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: '14px', color: COLORS.textMuted }}>المستشارون: <span style={{ fontWeight: 'bold', color: COLORS.text }}>{consultants.length}</span></p>
        <button onClick={() => { setEditItem(null); setShowModal(true); }} style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 500, border: 'none', cursor: 'pointer' }}>إضافة مستشار</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {consultants.map((c) => (
          <div key={c.consultant_id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', borderRadius: '12px', border: `1px solid ${COLORS.border}`, backgroundColor: c.is_active ? 'white' : COLORS.bgSecondary, opacity: c.is_active ? 1 : 0.6 }}>
            <img src={c.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name)}&background=1B3A6B&color=fff`} alt={c.name} style={{ width: '48px', height: '48px', borderRadius: '50%', border: `1px solid ${COLORS.border}`, objectFit: 'cover', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 500, fontSize: '14px' }}>{c.name}</span>
                <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', backgroundColor: c.is_active ? COLORS.successBg : COLORS.bgSecondary, color: c.is_active ? COLORS.success : COLORS.textMuted }}>{c.is_active ? "نشط" : "غير نشط"}</span>
                <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '9999px', backgroundColor: COLORS.warningBg, color: COLORS.warning }}>{c.rating} ★</span>
              </div>
              <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0 }}>{c.title} • {c.city} • {c.experience_years} سنة</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button onClick={() => { setEditItem(c); setShowModal(true); }} style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '12px', backgroundColor: 'white', cursor: 'pointer' }}>تعديل</button>
              <button onClick={() => toggleActive(c.consultant_id, c.is_active)} style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', border: 'none', cursor: 'pointer', backgroundColor: c.is_active ? COLORS.errorBg : COLORS.successBg, color: c.is_active ? COLORS.error : COLORS.success }}>{c.is_active ? "تعطيل" : "تفعيل"}</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && <ConsultantModal consultant={editItem} onSave={handleSave} onClose={() => setShowModal(false)} />}
    </div>
  );
}

function ConsultantModal({ consultant, onSave, onClose }) {
  const [form, setForm] = useState({
    name: consultant?.name || "",
    title: consultant?.title || "",
    consultation_fields: consultant?.consultation_fields?.join(", ") || "",
    experience_years: consultant?.experience_years || 5,
    rating: consultant?.rating || 4.5,
    city: consultant?.city || "الرياض",
    bio: consultant?.bio || "",
    is_active: consultant?.is_active ?? true,
  });

  const inputStyle = { width: '100%', height: '36px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, padding: '0 12px', fontSize: '14px', outline: 'none', backgroundColor: COLORS.bgSecondary };

  return (
    <Modal title={consultant ? "تعديل مستشار" : "إضافة مستشار جديد"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, consultation_fields: form.consultation_fields.split(",").map(s => s.trim()).filter(Boolean), experience_years: parseInt(form.experience_years), rating: parseFloat(form.rating) }); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>الاسم *</label>
            <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>المسمى الوظيفي *</label>
            <input type="text" value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} required style={inputStyle} />
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>مجالات الاستشارة * (مفصولة بفاصلة)</label>
          <input type="text" value={form.consultation_fields} onChange={(e) => setForm(p => ({ ...p, consultation_fields: e.target.value }))} required placeholder="التسويق, المبيعات" style={inputStyle} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>المدينة</label>
            <input type="text" value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>سنوات الخبرة</label>
            <input type="number" min="1" value={form.experience_years} onChange={(e) => setForm(p => ({ ...p, experience_years: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>التقييم</label>
            <input type="number" min="1" max="5" step="0.1" value={form.rating} onChange={(e) => setForm(p => ({ ...p, rating: e.target.value }))} style={inputStyle} />
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>نبذة</label>
          <textarea value={form.bio} onChange={(e) => setForm(p => ({ ...p, bio: e.target.value }))} rows={2} style={{ ...inputStyle, height: 'auto', padding: '8px 12px', resize: 'none' }} />
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
          <button type="submit" style={{ flex: 1, height: '40px', borderRadius: '8px', backgroundColor: COLORS.primary, color: 'white', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>{consultant ? "حفظ" : "إضافة"}</button>
          <button type="button" onClick={onClose} style={{ flex: 1, height: '40px', borderRadius: '8px', border: `1px solid ${COLORS.border}`, fontSize: '14px', backgroundColor: 'white', cursor: 'pointer' }}>إلغاء</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("requests");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
        setStats(res.data);
      } catch (e) { console.error(e); }
    };
    loadStats();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("hala_admin_token");
    localStorage.removeItem("hala_admin_user");
    navigate("/admin/login");
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: COLORS.bg }} dir="rtl">
      {/* Header */}
      <header style={{ backgroundColor: 'white', borderBottom: `1px solid ${COLORS.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
            <img src="/hala-44.png" alt="هلا" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div>
            <h1 style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.primary, margin: 0 }}>لوحة إدارة هلا</h1>
            <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: 0 }}>هلا</p>
          </div>
        </div>
        <button onClick={handleLogout} style={{ fontSize: '12px', color: COLORS.textMuted, border: `1px solid ${COLORS.border}`, padding: '6px 12px', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>تسجيل خروج</button>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Stats */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            {[
              { label: "إجمالي الطلبات", value: stats.total_requests, color: COLORS.primary },
              { label: "طلبات جديدة", value: stats.new_requests, color: '#2563eb' },
              { label: "خدمات نشطة", value: stats.active_services, color: COLORS.success },
              { label: "فئات نشطة", value: stats.active_categories, color: '#7c3aed' },
              { label: "قواعد نشطة", value: stats.active_rules, color: COLORS.warning },
              { label: "مستشارون", value: stats.active_consultants, color: '#e11d48' },
            ].map((s) => (
              <div key={s.label} style={{ backgroundColor: 'white', borderRadius: '12px', border: `1px solid ${COLORS.border}`, padding: '16px', textAlign: 'center' }}>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: s.color, margin: 0 }}>{s.value}</p>
                <p style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '4px' }}>{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs Container */}
        <div style={{ backgroundColor: 'white', borderRadius: '16px', border: `1px solid ${COLORS.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '16px 16px 0 16px', borderBottom: `1px solid ${COLORS.border}`, overflowX: 'auto' }}>
            <Tab active={activeTab === "requests"} onClick={() => setActiveTab("requests")}>الطلبات</Tab>
            <Tab active={activeTab === "categories"} onClick={() => setActiveTab("categories")}>الفئات</Tab>
            <Tab active={activeTab === "services"} onClick={() => setActiveTab("services")}>الخدمات</Tab>
            <Tab active={activeTab === "rules"} onClick={() => setActiveTab("rules")}>قواعد التعيين</Tab>
            <Tab active={activeTab === "consultants"} onClick={() => setActiveTab("consultants")}>المستشارون</Tab>
          </div>

          <div style={{ padding: '20px' }}>
            {activeTab === "requests" && <RequestsTab />}
            {activeTab === "categories" && <CategoriesTab />}
            {activeTab === "services" && <ServicesTab />}
            {activeTab === "rules" && <RulesTab />}
            {activeTab === "consultants" && <ConsultantsTab />}
          </div>
        </div>
      </main>
    </div>
  );
}
